<template>
  <section
    class="assistant"
    :class="`assistant--${brand.toLowerCase()}`"
    :style="{ '--assistant-dock': `${dockHeight}px` }"
    :aria-label="t('assistant.title')"
  >
    <!-- App bar: back to search on the left, call me on the right; stays visible while scrolling. -->
    <div class="assistant-bar">
      <span class="assistant-bar__start"><slot name="action" /></span>
      <button
        v-if="callOut"
        type="button"
        class="assistant-call-btn"
        :aria-expanded="callOpen"
        aria-haspopup="dialog"
        @click="openCall"
      >
        <svg class="assistant-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.11 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"
          />
        </svg>
        {{ t('assistant.callMe') }}
      </button>
    </div>

    <article v-if="$slots.default" class="assistant-shipment" :aria-label="t('assistant.shipmentLead')">
      <header class="assistant-shipment__from">
        <span class="assistant-avatar" aria-hidden="true">
          <img v-if="avatarSrc" :src="avatarSrc" alt="" />
          <template v-else>{{ brand.charAt(0) }}</template>
        </span>
        <strong>{{ brand }}</strong>
        <span class="assistant-shipment__lead">{{ t('assistant.shipmentLead') }}</span>
      </header>
      <div class="assistant-shipment__card">
        <slot />
      </div>
    </article>

    <div ref="thread" class="assistant-thread" role="log" aria-live="polite">
      <div class="assistant-msg assistant-msg--bot">
        <span class="assistant-avatar" aria-hidden="true">
          <img v-if="avatarSrc" :src="avatarSrc" alt="" />
          <template v-else>{{ brand.charAt(0) }}</template>
        </span>
        <div class="assistant-msg__body">
          <p class="assistant-bubble">{{ t('assistant.intro') }}</p>
          <p class="assistant-msg__hint">
            {{ t('assistant.subtitle', { brand }) }}.
            <template v-if="!verified">{{ t('assistant.verify.hint') }}</template>
          </p>
          <p v-if="demoCode && !verified" class="assistant-demo">
            <span class="assistant-demo__tag">{{ t('assistant.demo.tag') }}</span>
            <span>{{ t('assistant.demo.postal') }} <strong>{{ demoCode }}</strong></span>
            <button type="button" class="assistant-demo__use" @click="ask(demoCode)">
              {{ t('assistant.demo.use') }}
            </button>
          </p>
        </div>
      </div>

      <template v-for="m in messages" :key="m.id">
        <p v-if="m.role === 'system'" class="assistant-note" :class="`assistant-note--${m.tone || 'info'}`">
          <svg v-if="m.tone === 'success'" class="assistant-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20 6 9 17l-5-5" />
          </svg>
          {{ m.text }}
        </p>
        <div v-else-if="m.role === 'user'" class="assistant-msg assistant-msg--user">
          <p class="assistant-bubble">
            <span v-if="m.channel === 'phone'" class="assistant-msg__via">{{ t('assistant.viaPhone') }}</span>
            {{ m.text || '…' }}
          </p>
        </div>
        <div v-else class="assistant-msg assistant-msg--bot">
          <span class="assistant-avatar" aria-hidden="true">
            <img v-if="avatarSrc" :src="avatarSrc" alt="" />
            <template v-else>{{ brand.charAt(0) }}</template>
          </span>
          <div class="assistant-msg__body">
            <p v-if="m.text" class="assistant-bubble">
              <span v-if="m.channel === 'phone'" class="assistant-msg__via">{{ t('assistant.viaPhone') }}</span>
              {{ m.text }}
            </p>
            <p v-else-if="!m.done" class="assistant-bubble assistant-typing">
              <span class="assistant-dots" aria-hidden="true"><i /><i /><i /></span>{{ m.status }}
            </p>
            <template v-for="(c, i) in m.cards" :key="i">
              <AssistantEmailDraft
                v-if="c.kind === 'email_draft'"
                :card="c"
                :state="drafts[String(c.utkast)]"
                @send="sendDraft"
              />
              <AssistantCard v-else :card="c" :brand="brand" @forward="ask(t('assistant.mail.startCase'))" />
            </template>
          </div>
        </div>
      </template>
    </div>

    <div class="assistant-spacer" aria-hidden="true" />

    <!-- On <body>, so the section's full-width band (clip-path) never clips the fixed composer. -->
    <Teleport to="body">
      <div ref="dock" class="assistant-dock" :class="`assistant--${brand.toLowerCase()}`">
        <div class="assistant-dock__inner">
          <div v-if="showChips" class="assistant-chips">
            <button v-for="q in suggestions" :key="q" type="button" class="assistant-chip" @click="ask(q)">
              {{ q }}
            </button>
          </div>
          <form class="assistant-composer" :class="{ live }" @submit.prevent="send">
            <button
              type="button"
              class="assistant-mic"
              :class="{ on: live }"
              :aria-label="live ? t('assistant.interrupt') : t('assistant.talk')"
              :aria-pressed="live"
              :disabled="!voice"
              @click="toggleMic"
            >
              <svg class="assistant-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3" />
              </svg>
            </button>
            <label class="assistant-sr" for="assistant-input">{{ placeholder }}</label>
            <input
              id="assistant-input"
              v-model="draft"
              type="text"
              autocomplete="off"
              enterkeyhint="send"
              maxlength="500"
              :placeholder="placeholder"
              @focus="ensureClient"
            />
            <button v-if="live" type="button" class="assistant-stop" @click="stopMic">{{ t('assistant.end') }}</button>
            <button
              type="submit"
              class="assistant-send"
              :aria-label="t('assistant.send')"
              :disabled="!draft.trim() || verifying"
            >
              <svg class="assistant-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 19V5M5 12l7-7 7 7" />
              </svg>
            </button>
          </form>
        </div>
      </div>
      <dialog
        ref="callDialog"
        class="assistant-dialog"
        :class="`assistant--${brand.toLowerCase()}`"
        aria-labelledby="assistant-call-title"
        @close="callOpen = false"
        @click="onDialogClick"
      >
        <form v-if="callOpen" @submit.prevent="submitCall">
          <h2 id="assistant-call-title">{{ t('assistant.callTitle', { brand }) }}</h2>
          <template v-if="callStep === 'number'">
            <p class="assistant-dialog__text">{{ t('assistant.callHint') }}</p>
            <label for="assistant-call-to">{{ t('assistant.callLabel') }}</label>
            <input
              id="assistant-call-to"
              v-model="callTo"
              type="tel"
              inputmode="tel"
              autocomplete="tel"
              placeholder="070-123 45 67"
            />
          </template>
          <template v-else>
            <p class="assistant-dialog__text">{{ t('assistant.callNeedsPostal', { number: callTo.trim() }) }}</p>
            <p v-if="demoCode" class="assistant-demo">
              <span class="assistant-demo__tag">{{ t('assistant.demo.tag') }}</span>
              <span>{{ t('assistant.demo.postal') }} <strong>{{ demoCode }}</strong></span>
              <button type="button" class="assistant-demo__use" @click="callPostal = demoCode">
                {{ t('assistant.demo.use') }}
              </button>
            </p>
            <label for="assistant-call-postal">{{ t('assistant.placeholderVerify') }}</label>
            <input
              id="assistant-call-postal"
              v-model="callPostal"
              type="text"
              inputmode="numeric"
              autocomplete="postal-code"
              maxlength="12"
            />
            <button type="button" class="assistant-dialog__link" @click="callStep = 'number'">
              {{ t('assistant.callOtherNumber') }}
            </button>
          </template>
          <p v-if="callMessage" class="assistant-dialog__error" role="alert">{{ callMessage }}</p>
          <div class="assistant-dialog__actions">
            <button type="button" class="assistant-dialog__cancel" @click="closeCall">
              {{ t('assistant.cancel') }}
            </button>
            <button
              type="submit"
              class="assistant-dialog__go"
              :disabled="callStep === 'number' ? !callTo.trim() || calling : !callPostal.trim() || verifying"
            >
              <svg class="assistant-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.11 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"
                />
              </svg>
              {{ callStep === 'number' ? t('assistant.callNow') : t('assistant.callVerifyAndCall') }}
            </button>
          </div>
        </form>
      </dialog>
    </Teleport>
  </section>
</template>

<script setup lang="ts">
import type { AssistantCard as Card, AssistantEvent } from '@/services/AssistantClient'
import type { DraftState } from '@/views/Components/Assistant/AssistantEmailDraft.vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AssistantCard from '@/views/Components/Assistant/AssistantCard.vue'
import AssistantClient from '@/services/AssistantClient'
import AssistantEmailDraft from '@/views/Components/Assistant/AssistantEmailDraft.vue'

interface Message {
  id: string
  role: 'user' | 'bot' | 'system'
  text: string
  cards: Card[]
  channel?: string
  status?: string
  done?: boolean
  tone?: 'info' | 'success' | 'danger'
}

// A typed postal code (e.g. "123 45", "00100", "0150") unlocks the conversation instead of being sent as a question.
const POSTAL_CODE = /^\s*\d{2,3}\s?\d{2,3}\s*$/

const props = defineProps<{ trackingId: string; brand: string; baseUrl: string; demoPostalCode?: string }>()
// "verified": the recipient's postal code matched; the page may show the full delivery address
const emit = defineEmits<{ verified: [] }>()
// Dev demo only: the recipient's postal code, shown so testers can unlock without looking it up.
const demoCode = computed(() => {
  const code = (props.demoPostalCode || '').replace(/\s/g, '')
  return /^\d{5}$/.test(code) ? `${code.slice(0, 3)} ${code.slice(3)}` : code
})
const { t, tm, locale } = useI18n()
const avatarSrc =
  props.brand.toLowerCase() === '21grams' ? new URL('../../../assets/images/logo.svg', import.meta.url).href : ''

const verified = ref(false)
watch(verified, (value) => value && emit('verified'))
const verifying = ref(false)
const askedSinceVerify = ref(false)
const voice = ref(true)
const callOut = ref(false)
const live = ref(false)
const draft = ref('')
const callOpen = ref(false)
const callStep = ref<'number' | 'postal'>('number')
const callPostal = ref('')
const calling = ref(false)
const callTo = ref('')
const callMessage = ref('')
const drafts = ref<Record<string, DraftState>>({})
const messages = ref<Message[]>([])
const thread = ref<HTMLElement | null>(null)
const dock = ref<HTMLElement | null>(null)
const dockHeight = ref(96)
const suggestions = computed(() => tm('assistant.suggestions') as string[])
const placeholder = computed(() => {
  if (live.value) return t('assistant.listening')
  return verified.value ? t('assistant.placeholder') : t('assistant.placeholderVerify')
})
const showChips = computed(
  () => verified.value && !askedSinceVerify.value && !messages.value.some((m) => m.role === 'bot' && !m.done),
)

let client: AssistantClient | null = null
let resizeObserver: ResizeObserver | null = null

const ensureClient = (): AssistantClient => {
  if (!client) {
    client = new AssistantClient(props.baseUrl, props.trackingId, props.brand, locale.value)
    client.on(handle)
    client.connect()
  }
  return client
}

// Keeps the newest message just above the docked composer.
const scrollToEnd = () =>
  nextTick(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    thread.value?.lastElementChild?.scrollIntoView?.({ block: 'end', behavior: reduce ? 'auto' : 'smooth' })
  })

const note = (text: string, tone: Message['tone'] = 'info') => {
  messages.value.push({ id: `n-${Date.now()}-${messages.value.length}`, role: 'system', text, cards: [], tone })
  scrollToEnd() // e.g. "Vi ringer upp … nu" after the call dialog closes
}

const turn = (id: string, channel?: string): Message => {
  let m = messages.value.find((x) => x.id === id)
  if (!m) {
    m = { id, role: 'bot', text: '', cards: [], channel }
    messages.value.push(m)
  }
  return m
}

const handle = (e: AssistantEvent) => {
  let scroll = true
  switch (e.type) {
    case 'hello':
      verified.value = !!e.verified
      voice.value = e.voice !== false
      callOut.value = !!e.callOut
      scroll = false
      break
    case 'user_pending':
    case 'user': {
      if (!e.item && e.channel === 'web') return // typed text is already shown
      const id = `u-${e.item || Math.random()}`
      const existing = messages.value.find((x) => x.id === id)
      if (existing) {
        if (e.text) existing.text = e.text
      } else {
        messages.value.push({ id, role: 'user', text: e.text || '…', cards: [], channel: e.channel })
      }
      if (verified.value) askedSinceVerify.value = true
      break
    }
    case 'assistant_start':
      turn(e.id || '', e.channel)
      break
    case 'status':
      turn(e.id || '').status = e.label
      scroll = false
      break
    case 'assistant_delta':
      turn(e.id || '').text += e.text || ''
      scroll = false
      break
    case 'assistant_reset':
      turn(e.id || '').text = ''
      scroll = false
      break
    case 'assistant_end': {
      const m = turn(e.id || '', e.channel)
      if (e.text) m.text = e.text
      m.done = true
      break
    }
    case 'card':
      if (e.card) turn(e.id || '').cards.push(e.card)
      break
    case 'verified':
      if (!verified.value) note(t('assistant.verify.done'), 'success')
      verified.value = true
      verifying.value = false
      askedSinceVerify.value = false
      if (callOpen.value && callStep.value === 'postal') void callMe() // the customer already pressed Ring
      break
    case 'verify_failed':
      verifying.value = false
      if (callOpen.value && callStep.value === 'postal') {
        callMessage.value =
          e.remaining === null || e.remaining === undefined
            ? e.message || t('assistant.verify.failed')
            : t('assistant.verify.failedRemaining', { n: e.remaining })
        break
      }
      note(
        e.remaining === null || e.remaining === undefined
          ? e.message || t('assistant.verify.failed')
          : t('assistant.verify.failedRemaining', { n: e.remaining }),
        'danger',
      )
      break
    case 'email_sent':
      drafts.value[e.draft || ''] = { status: 'sent' }
      note(t('assistant.mail.sentNote', { store: e.butik || '' }), 'success')
      break
    case 'email_failed':
      drafts.value[e.draft || ''] = { status: 'failed', message: e.message }
      scroll = false
      break
    case 'call':
      if (e.state) note(t(`assistant.call_${e.state}`))
      if (e.state === 'started') live.value = false
      break
    case 'error':
      note(e.message || t('assistant.error'), 'danger')
      verifying.value = false
      break
    default:
      return
  }
  if (scroll) scrollToEnd()
}

const ask = (text: string) => {
  messages.value.push({ id: `t-${Date.now()}`, role: 'user', text, cards: [] })
  if (!verified.value && POSTAL_CODE.test(text)) {
    verifying.value = true
    ensureClient().verify(text)
  } else {
    if (verified.value) askedSinceVerify.value = true
    ensureClient().ask(text)
  }
  scrollToEnd()
}

const send = () => {
  const text = draft.value.trim()
  if (!text || verifying.value) return
  draft.value = ''
  ask(text)
}

const toggleMic = async () => {
  const c = ensureClient()
  if (live.value) {
    c.interrupt() // a tap while live interrupts the assistant; "End" stops the voice session
    return
  }
  live.value = await c.startMic()
  if (!live.value) note(t('assistant.micBlocked'), 'danger')
  scrollToEnd()
}

const stopMic = () => {
  client?.stopMic()
  live.value = false
}

const sendDraft = (draft: string, body: string, replyTo: string) => {
  drafts.value[draft] = { status: 'sending' }
  ensureClient().send({ type: 'send_email', draft, body, replyTo })
}

const callDialog = ref<HTMLDialogElement | null>(null)

// Native modal dialog (focus stays inside, Esc closes); a plain open attribute where showModal is missing.
const openCall = () => {
  callOpen.value = true
  callStep.value = 'number'
  callMessage.value = ''
  callPostal.value = ''
  ensureClient() // the call needs the session id from the server
  nextTick(() => {
    const dialog = callDialog.value
    if (!dialog) return
    try {
      dialog.showModal()
    } catch {
      dialog.setAttribute('open', '')
    }
    document.getElementById('assistant-call-to')?.focus()
  })
}

const closeCall = () => {
  callOpen.value = false
  const dialog = callDialog.value
  if (!dialog) return
  try {
    dialog.close()
  } catch {
    dialog.removeAttribute('open')
  }
}

const onDialogClick = (event: MouseEvent) => {
  if (event.target === callDialog.value) closeCall() // a click on the backdrop
}

const callApi = async (path: string, to: string) => {
  const r = await fetch(new URL(path, props.baseUrl).toString(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session: client?.sessionId, to }),
  })
  return { ok: r.ok, body: (await r.json()) as { error?: string; needPostal?: boolean } }
}

const verifyForCall = () => {
  if (!callPostal.value.trim()) return
  callMessage.value = ''
  verifying.value = true
  ensureClient().verify(callPostal.value)
}

const submitCall = () => {
  if (callStep.value === 'number') void callMe()
  else verifyForCall()
}

// Pressing Ring is the confirmation. The server calls at once if the number is on the shipment or the postal code
// is verified; otherwise the panel asks for the postal code and calls as soon as it is verified.
const callMe = async () => {
  if (!callTo.value.trim()) return
  callMessage.value = ''
  calling.value = true
  try {
    const { ok, body } = await callApi('/api/call', callTo.value)
    if (ok) {
      closeCall()
      note(t('assistant.calling', { number: callTo.value.trim() }))
    } else if (body.needPostal && !verified.value) {
      callStep.value = 'postal'
    } else {
      callMessage.value = body.error || t('assistant.error')
    }
  } catch {
    callMessage.value = t('assistant.error')
  } finally {
    calling.value = false
  }
}

// A plain status request (no AI session) so "Ring mig" and the mic show before the first interaction.
const loadAvailability = async () => {
  try {
    const r = await fetch(new URL('/health', props.baseUrl).toString())
    const health = (await r.json()) as { voice?: boolean; phone?: boolean }
    if (!client) {
      voice.value = health.voice !== false
      callOut.value = !!health.phone
    }
  } catch {
    // the chat still works; the call button just waits for the connection
  }
}

onMounted(() => {
  void loadAvailability()
  if (!dock.value || typeof ResizeObserver === 'undefined') return
  resizeObserver = new ResizeObserver(() => {
    if (!dock.value || dock.value.offsetHeight === dockHeight.value) return
    dockHeight.value = dock.value.offsetHeight
    if (messages.value.length) scrollToEnd() // quick replies appeared or went away: keep the last message visible
  })
  resizeObserver.observe(dock.value)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  client?.close()
})
</script>

<style lang="scss" scoped>
// 21grams.com tokens: dark teal for primary actions and text, orange for the call to action,
// teal for progress, warm grey section band, pill-shaped buttons. The dock is teleported, so it gets them too.
.assistant,
.assistant-dock,
.assistant-dialog {
  --a-primary: #012b2b;
  --a-primary-hover: #024545;
  --a-accent: #ff4e10;
  --a-accent-hover: #f53004;
  --a-progress: #058880;
  --a-band: #f0f0ef;
  --a-bg: #ffffff;
  --a-line: #e1e1dc;
  --a-field: #c8c8c8;
  --a-text: #151515;
  --a-muted: #66665e;
  --a-success-bg: #e2f1ef;
  --a-danger: #c62828;
  --a-danger-bg: #fdecec;
}
.assistant {
  position: relative;
  padding-top: 0;
  background: var(--a-band);
  // Full-width band without widening the page (no horizontal scroll).
  box-shadow: 0 0 0 100vmax var(--a-band);
  clip-path: inset(0 -100vmax);
  color: var(--a-text);
  font-family: inherit;
}
.assistant--charma {
  --a-primary: #16261c;
  --a-primary-hover: #0d1711;
  --a-accent: #16261c;
  --a-accent-hover: #0d1711;
  --a-progress: #16261c;
  --a-band: #f6efe2;
  --a-line: #e6dccb;
  --a-text: #1c2b22;
  --a-muted: #6b6e61;
  --a-success-bg: #e8efe6;
}
.assistant--virea {
  --a-primary: #002819;
  --a-primary-hover: #00402a;
  --a-accent: #2c7022;
  --a-accent-hover: #235a1b;
  --a-progress: #2c7022;
  --a-band: #f5f9e1;
  --a-success-bg: #e6f1df;
}

.assistant-icon {
  width: 20px;
  height: 20px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  flex: none;
}
.assistant-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

// ---- tracking details as the first message -----------------------------------------------------
.assistant-shipment {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.assistant-shipment__from {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  font-size: 14px;
  color: var(--a-muted);
  strong {
    color: var(--a-text);
    font-weight: 600;
  }
}
.assistant-shipment__card {
  padding: 8px 24px 4px;
  border-radius: 16px;
  background: var(--a-bg);
}

// ---- app bar: back to search (left) and call me (right) ------------------------------------
.assistant-bar {
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 60px;
  margin-bottom: 4px;
  background: var(--a-band);
  // full-width band behind the bar without widening the page
  box-shadow: 0 0 0 100vmax var(--a-band);
  clip-path: inset(0 -100vmax);
}
.assistant-call-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: none;
  min-height: 40px;
  padding: 0 16px 0 14px;
  border: 1px solid var(--a-primary);
  border-radius: 40px;
  background: var(--a-bg);
  color: var(--a-primary);
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
  .assistant-icon {
    width: 18px;
    height: 18px;
  }
  &:hover,
  &[aria-expanded='true'] {
    background: var(--a-primary);
    color: #fff;
  }
  &:focus-visible {
    outline: 2px solid var(--a-accent);
    outline-offset: 2px;
  }
}

// ---- call me popup ---------------------------------------------------------------------------
.assistant-dialog {
  // centred explicitly: the site's reset removes the browser's default dialog centring
  position: fixed;
  inset: 0;
  margin: auto;
  width: min(420px, calc(100vw - 32px));
  height: fit-content;
  max-height: calc(100dvh - 32px);
  overflow: auto;
  padding: 0;
  border: 0;
  border-radius: 20px;
  background: var(--a-bg);
  color: var(--a-text);
  box-shadow: 0 24px 64px rgb(1 43 43 / 24%);
  &::backdrop {
    background: rgb(1 43 43 / 45%);
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 24px;
  }
  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 500;
    text-transform: none;
  }
  label {
    margin-top: 4px;
    font-size: 14px;
    font-weight: 500;
  }
  input {
    width: 100%;
    height: 48px;
    padding: 0 18px;
    border: 1px solid var(--a-field);
    border-radius: 40px;
    color: var(--a-text);
    font-size: 16px;
    &:focus-visible {
      outline: none;
      border-color: var(--a-primary);
      box-shadow: 0 0 0 3px rgb(1 43 43 / 12%);
    }
  }
}
.assistant-dialog__text {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--a-muted);
}
.assistant-dialog__error {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--a-danger);
}
.assistant-dialog__link {
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: none;
  color: var(--a-primary);
  font-size: 14px;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}
.assistant-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
}
.assistant-dialog__cancel,
.assistant-dialog__go {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 20px;
  border-radius: 40px;
  font-weight: 500;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid var(--a-accent);
    outline-offset: 2px;
  }
}
.assistant-dialog__cancel {
  border: 1px solid var(--a-line);
  background: var(--a-bg);
  color: var(--a-text);
  &:hover {
    border-color: var(--a-primary);
  }
}
.assistant-dialog__go {
  border: 0;
  background: var(--a-accent);
  color: #fff;
  .assistant-icon {
    width: 18px;
    height: 18px;
  }
  &:hover:not(:disabled) {
    background: var(--a-accent-hover);
  }
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
}

// ---- dev demo hint ----------------------------------------------------------------------------
.assistant-demo {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 10px 12px;
  border: 1px dashed var(--a-accent);
  border-radius: 12px;
  background: color-mix(in srgb, var(--a-accent) 6%, var(--a-bg));
  font-size: 14px;
  strong {
    font-size: 16px;
    letter-spacing: 0.04em;
  }
}
.assistant-demo__tag {
  padding: 2px 8px;
  border-radius: 40px;
  background: var(--a-accent);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.assistant-demo__use {
  margin-left: auto;
  min-height: 32px;
  padding: 0 14px;
  border: 1px solid var(--a-primary);
  border-radius: 40px;
  background: var(--a-bg);
  color: var(--a-primary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    background: var(--a-primary);
    color: #fff;
  }
}

// ---- conversation ------------------------------------------------------------------------------
.assistant-thread {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
  > * {
    scroll-margin-bottom: calc(var(--assistant-dock) + 16px);
  }
}
.assistant-msg {
  display: flex;
  min-width: 0;
}
.assistant-msg--bot {
  align-self: flex-start;
  align-items: flex-start;
  gap: 10px;
  max-width: 92%;
}
.assistant-msg--user {
  align-self: flex-end;
  max-width: 82%;
}
.assistant-msg__body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}
.assistant-avatar {
  display: grid;
  place-items: center;
  flex: none;
  width: 32px;
  height: 32px;
  overflow: hidden;
  background: var(--a-accent);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  img {
    display: block;
    width: 100%;
    height: 100%;
    transform: scale(1.7); // the logo file has wide margins around the mark
  }
}
.assistant-bubble {
  margin: 0;
  padding: 12px 16px;
  font-size: 15px;
  line-height: 1.55;
  overflow-wrap: anywhere;
}
.assistant-msg--bot .assistant-bubble {
  border-radius: 0 18px 18px 18px;
  background: var(--a-bg);
}
.assistant-msg--user .assistant-bubble {
  border-radius: 18px 0 18px 18px;
  background: var(--a-primary);
  color: #fff;
}
.assistant-msg__hint {
  margin: 0;
  padding: 0 4px;
  font-size: 13px;
  color: var(--a-muted);
}
.assistant-msg__via {
  display: block;
  font-size: 12px;
  opacity: 0.75;
}
.assistant-note {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: center;
  margin: 0;
  padding: 6px 14px;
  border-radius: 40px;
  background: var(--a-bg);
  color: var(--a-muted);
  font-size: 13px;
  text-align: center;
  .assistant-icon {
    width: 16px;
    height: 16px;
  }
}
.assistant-note--success {
  background: var(--a-success-bg);
  color: var(--a-progress);
  font-weight: 600;
}
.assistant-note--danger {
  background: var(--a-danger-bg);
  color: var(--a-danger);
}
.assistant-typing {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--a-muted);
}
.assistant-dots {
  display: inline-flex;
  gap: 4px;
  i {
    width: 6px;
    height: 6px;
    border-radius: 99px;
    background: currentColor;
    animation: assistant-blink 1.2s infinite ease-in-out;
  }
  i:nth-child(2) {
    animation-delay: 0.15s;
  }
  i:nth-child(3) {
    animation-delay: 0.3s;
  }
}
.assistant-spacer {
  height: calc(var(--assistant-dock) + 32px);
}

// ---- docked composer ---------------------------------------------------------------------------
.assistant-dock {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 50;
  padding: 12px 0 max(12px, env(safe-area-inset-bottom));
  border-top: 1px solid var(--a-line);
  background: rgb(255 255 255 / 94%);
  backdrop-filter: saturate(1.4) blur(14px);
}
.assistant-dock__inner {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 638px;
  margin: 0 auto;
  padding: 0 15px;
}
.assistant-chips {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
  touch-action: pan-x;
  &::-webkit-scrollbar {
    display: none;
  }
}
.assistant-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: none;
  min-height: 40px;
  padding: 0 18px;
  border: 1px solid var(--a-primary);
  border-radius: 40px;
  background: var(--a-bg);
  color: var(--a-primary);
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
  &:hover {
    background: var(--a-primary);
    color: #fff;
  }
  &:focus-visible {
    outline: 2px solid var(--a-accent);
    outline-offset: 2px;
  }
}
.assistant-composer {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 4px;
  border: 1px solid var(--a-field);
  border-radius: 40px;
  background: var(--a-bg);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
  &:focus-within {
    border-color: var(--a-primary);
    box-shadow: 0 0 0 3px rgb(1 43 43 / 10%);
  }
  &.live {
    border-color: var(--a-accent);
  }
  input {
    flex: 1;
    min-width: 0;
    width: 0;
    height: 48px;
    padding: 0 8px;
    border: 0;
    background: transparent;
    color: var(--a-text);
    font-size: 16px;
    &:focus {
      outline: none;
    }
    &::placeholder {
      color: var(--a-muted);
    }
  }
}
.assistant-mic,
.assistant-send {
  display: grid;
  place-items: center;
  flex: none;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  &:focus-visible {
    outline: 2px solid var(--a-accent);
    outline-offset: 2px;
  }
}
.assistant-mic {
  background: var(--a-band);
  color: var(--a-primary);
  &:hover:not(:disabled) {
    background: var(--a-line);
  }
  &.on {
    background: var(--a-accent);
    color: #fff;
    animation: assistant-pulse 1.6s infinite ease-out;
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
}
.assistant-send {
  background: var(--a-accent);
  color: #fff;
  &:hover:not(:disabled) {
    background: var(--a-accent-hover);
  }
  &:disabled {
    background: var(--a-band);
    color: var(--a-muted);
    cursor: default;
  }
}
.assistant-stop {
  flex: none;
  min-height: 36px;
  padding: 0 14px;
  border: 0;
  border-radius: 40px;
  background: var(--a-danger-bg);
  color: var(--a-danger);
  font-weight: 500;
  cursor: pointer;
}

// ---- cards -------------------------------------------------------------------------------------
:deep(.assistant-card) {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px;
  border-radius: 12px;
  background: var(--a-bg);
  p {
    margin: 0;
  }
}
:deep(.assistant-card__eyebrow) {
  font-size: 13px;
  font-weight: 500;
  color: var(--a-muted);
}
:deep(.assistant-card__title) {
  font-size: 18px;
  font-weight: 500;
  line-height: 1.3;
}
:deep(.assistant-card__strong) {
  font-weight: 600;
}
:deep(.assistant-card__muted) {
  font-size: 13px;
  color: var(--a-muted);
  overflow-wrap: anywhere;
}
:deep(.assistant-card__note) {
  padding: 10px 12px;
  border-left: 3px solid var(--a-accent);
  background: var(--a-band);
  font-size: 14px;
}
:deep(.assistant-card__link) {
  align-self: flex-start;
  font-weight: 500;
  color: var(--a-primary);
  text-decoration: none;
  &::after {
    content: ' →';
  }
  &:hover {
    text-decoration: underline;
  }
}
:deep(.assistant-card__actions) {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  margin-top: 6px;
  .assistant-card__button {
    margin-top: 0;
  }
  .assistant-card__link::after {
    content: '';
  }
}
:deep(.assistant-card__button) {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 8px;
  min-height: 44px;
  margin-top: 6px;
  padding: 0 24px;
  border-radius: 40px;
  background: var(--a-primary);
  color: #fff;
  font-weight: 500;
  text-decoration: none;
  &::after {
    content: '→';
  }
  &:hover {
    background: var(--a-primary-hover);
    color: #fff;
  }
}
:deep(.assistant-steps) {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 4px;
  margin: 8px 0;
  padding: 0;
  list-style: none;
  li {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 12px;
    color: var(--a-muted);
  }
  li.done {
    color: var(--a-text);
  }
  li.current .assistant-steps__label {
    font-weight: 600;
  }
}
:deep(.assistant-steps__dot) {
  height: 4px;
  border-radius: 99px;
  background: var(--a-line);
}
:deep(.assistant-steps li.done .assistant-steps__dot) {
  background: var(--a-progress);
}
:deep(.assistant-timeline) {
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
  li {
    position: relative;
    padding: 0 0 12px 22px;
  }
  li::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 10px;
    bottom: -2px;
    width: 2px;
    background: var(--a-line);
  }
  li:last-child::before {
    display: none;
  }
  li::after {
    content: '';
    position: absolute;
    left: 0;
    top: 4px;
    width: 12px;
    height: 12px;
    border-radius: 99px;
    border: 2px solid var(--a-progress);
    background: var(--a-bg);
  }
  li.latest::after {
    background: var(--a-progress);
  }
}

// ---- e-mail draft to the store ---------------------------------------------------------------
:deep(.assistant-mail) {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
  label {
    font-size: 14px;
    font-weight: 500;
  }
}
:deep(.assistant-field) {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid var(--a-field);
  border-radius: 12px;
  background: var(--a-bg);
  color: var(--a-text);
  font: inherit;
  font-size: 16px;
  line-height: 1.5;
  resize: vertical;
  &:focus-visible {
    outline: none;
    border-color: var(--a-primary);
    box-shadow: 0 0 0 3px rgb(1 43 43 / 10%);
  }
}
:deep(input.assistant-field) {
  height: 48px;
  border-radius: 40px;
  padding: 0 18px;
}
:deep(button.assistant-card__button) {
  border: 0;
  cursor: pointer;
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
}
:deep(.assistant-card__error) {
  color: var(--a-danger);
  font-size: 14px;
}
:deep(.assistant-card .assistant-icon) {
  width: 20px;
  height: 20px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  flex: none;
}
:deep(.assistant-card__sent) {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--a-progress);
}

@keyframes assistant-blink {
  0%,
  100% {
    opacity: 0.25;
  }
  50% {
    opacity: 1;
  }
}
@keyframes assistant-pulse {
  0% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--a-accent) 45%, transparent);
  }
  100% {
    box-shadow: 0 0 0 12px color-mix(in srgb, var(--a-accent) 0%, transparent);
  }
}
@media (min-width: 600px) {
  .assistant-chips {
    flex-wrap: wrap;
    overflow: visible;
  }
}
@media (max-width: 481px) {
  .assistant-shipment__card {
    padding: 4px 16px 0;
  }
  .assistant-msg--bot {
    max-width: 100%;
  }
  .assistant-msg--user {
    max-width: 88%;
  }
}
@media (prefers-reduced-motion: reduce) {
  .assistant-dots i,
  .assistant-mic.on {
    animation: none;
  }
  .assistant-composer,
  .assistant-call-btn,
  .assistant-chip,
  .assistant-mic,
  .assistant-send {
    transition: none;
  }
}
</style>
